import { useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { hasRuntimePermission } from '@/utils/rbac';

export { hasRuntimePermission } from '@/utils/rbac';

export function useRbac() {
  const { user } = useAuth();
  const permissions = useMemo(() => user?.permissions ?? [], [user?.permissions]);
  const userRoles = useMemo(() => {
    if (!user) return [];
    if (!user.roleIds || user.roleIds.length === 0) return [{ _id: user.role, alias: user.role }];
    return user.roleIds.map((_id, index) => ({ _id, alias: index === 0 ? user.role : '' }));
  }, [user]);

  // Nhận thêm dạng MẢNG (ngữ nghĩa OR — thoả 1 key là đủ), khớp đúng `hasAny` bên dưới,
  // `PermissionGuard` (mode mặc định 'any') và `PrivateRoute` (`hasAny(requiredPermissions)`).
  //
  // Thêm vào để phục vụ giai đoạn chuyển tiếp `staff.*` → `hrm.*` (PR3/4): call site viết
  // `hasPermission(['hrm.contracts.manage'])` là chạy đúng ở CẢ hai phía —
  // trước khi backfill (tài khoản mới có `staff.*`) lẫn sau PR4 (chỉ còn `hrm.*`). Nếu thay vì
  // vậy mà đổi từng call site sang `hasAny` thì phải sửa cả dòng destructure
  // `const { hasPermission } = useRbac()` ở ~30 file — nhiều diff hơn mà không thêm gì.
  const hasPermission = useMemo(() => {
    return (key: string | readonly string[]) =>
      typeof key === 'string'
        ? hasRuntimePermission(permissions, key)
        : key.some((candidate) => hasRuntimePermission(permissions, candidate));
  }, [permissions]);

  const hasAny = useMemo(() => {
    return (keys: string[]) => keys.some((key) => hasRuntimePermission(permissions, key));
  }, [permissions]);

  return { permissions, userRoles, hasPermission, hasAny, isLoading: Boolean(user && !user.permissions) };
}
